import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import type { GetServerSidePropsContext } from 'next';

const cookieOptions = { path: '/' };

type CookieName =
  | 'token'
  // `ps_*` cookies are used for PartnerStack
  | 'ps_xid'
  | 'ps_partner_key'
  | 'ps_group_src'
  | 'blinq_viewer_id'
  | 'blinq_debug';

/**
 * A simple wrapper around `cookies-next`.
 *
 * The request context *must* be provided for server-side operations.
 *
 * @todo figure out the correct expiry to set.
 */
export const cookies = {
  set(name: CookieName, value: string, ctx?: GetServerSidePropsContext): void {
    setCookie(name, value, { ...cookieOptions, req: ctx?.req, res: ctx?.res });
  },
  delete(name: CookieName, ctx?: GetServerSidePropsContext): void {
    deleteCookie(name, { ...cookieOptions, req: ctx?.req, res: ctx?.res });
  },
  get(name: CookieName, ctx?: GetServerSidePropsContext): string | undefined {
    return getCookie(name, ctx);
  },
};

/**
 * Checks if debug mode is enabled for the current request via the `blinq_debug` cookie.
 *
 * This can be useful to add logging to cases that can be replicated in production, but not locally.
 *
 * **Enable debug mode:**
 * ```js
 * // Open web inspector in your browser (F12), then turn it on:
 * document.cookie = 'blinq_debug=1';
 * ```
 *
 * **Disable debug mode:**
 * ```js
 * // Turn it off:
 * document.cookie = 'blinq_debug=';
 * ```
 *
 * **Usage in server-side code:**
 * ```ts
 * import { isDebugRequest } from '@modules/auth/cookies';
 *
 * export const getServerSideProps = async (ctx) => {
 *   if (isDebugRequest(ctx)) {
 *     console.log('Debug mode enabled, logging extra information...');
 *   }
 * };
 * ```
 *
 * @param ctx - Next.js GetServerSidePropsContext for server-side operations
 * @returns true if debug mode is enabled, false otherwise
 */
export function isDebugRequest(ctx?: GetServerSidePropsContext): boolean {
  return (cookies.get('blinq_debug', ctx) ?? '') === '1';
}
